import ListPageController, { type StatCardDef } from "./mixins/ListPageController";
import type Table from "sap/ui/table/Table";
import type ODataListBinding from "sap/ui/model/odata/v4/ODataListBinding";
import type SearchField from "sap/m/SearchField";
import type DatePicker from "sap/m/DatePicker";
import type { Select$ChangeEvent } from "sap/m/Select";

interface VariantPreset {
	changeType?: "Command" | "DomainEvent" | "DataMutation";
	dateOffsetDays?: number;
}

const VARIANTS: Record<string, VariantPreset> = {
	"standard":     {},
	"last24h":      { dateOffsetDays: 1 },
	"last7days":    { dateOffsetDays: 7 },
	"commands":     { changeType: "Command" },
	"domainEvents": { changeType: "DomainEvent" }
};

// Karty statystyk nad tabelą — ten sam fragment StatCardsRow co Organizations/Dashboard.
// last24h liczy okno czasowe przy każdym refresh (stąd funkcja zamiast statycznego stringa).
const STATS: readonly StatCardDef[] = [
	{ key: "total", labelKey: "auditLog.stats.total.label", subtitleKey: "auditLog.stats.total.subtitle", state: "Neutral" },
	{ key: "last24h", labelKey: "auditLog.stats.last24h.label", subtitleKey: "auditLog.stats.last24h.subtitle", state: "None", filter: () => `OccurredAt ge ${new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString()}` },
	{ key: "commands", labelKey: "auditLog.stats.commands.label", subtitleKey: "auditLog.stats.commands.subtitle", state: "None", filter: "ChangeType eq 'Command'" },
	{ key: "domainEvents", labelKey: "auditLog.stats.domainEvents.label", subtitleKey: "auditLog.stats.domainEvents.subtitle", state: "None", filter: "ChangeType eq 'DomainEvent'" }
];

/**
 * @namespace computec.b2b.portal.controller
 */
export default class AuditLog extends ListPageController {
	private currentVariant = "standard";

	public override onInit(): void {
		void this.initStatsCards("AuditEntries", STATS);
		this.getRouter().getRoute("audit-log")?.attachPatternMatched(() => { void this.onShow(); });
	}

	private async onShow(): Promise<void> {
		const bundle = await this.getResourceBundle();
		this.setPageHeader({ title: bundle.getText("auditLog.title") ?? "Audit Log", subtitle: bundle.getText("auditLog.subtitle") ?? "" });
		void this.refreshStatsCards();
	}

	public onRefresh(): void {
		const binding = (this.byId("auditTable") as Table).getBinding("rows") as ODataListBinding | undefined;
		binding?.refresh();
		void this.refreshStatsCards();
	}

	public onVariantSelect(event: Select$ChangeEvent): void {
		this.currentVariant = event.getParameter("selectedItem")?.getKey() ?? "standard";
		this.onApplyFilters();
	}

	public onApplyFilters(): void {
		const binding = (this.byId("auditTable") as Table).getBinding("rows") as ODataListBinding | undefined;
		if (!binding) {
			return;
		}

		const actor = (this.byId("auditActorFilter") as SearchField).getValue().trim();
		const action = (this.byId("auditActionFilter") as SearchField).getValue().trim();
		const target = (this.byId("auditTargetFilter") as SearchField).getValue().trim();
		const from = (this.byId("auditFromFilter") as DatePicker).getValue();
		const to = (this.byId("auditToFilter") as DatePicker).getValue();

		// Raw $filter — easier than Filter[] when mixing strings, dates and variants.
		const parts: string[] = [];
		if (actor)  parts.push(`contains(ActorEmail,'${escape(actor)}')`);
		if (action) parts.push(`contains(Action,'${escape(action)}')`);
		if (target) parts.push(`contains(EntityType,'${escape(target)}')`);
		if (from)   parts.push(`OccurredAt ge ${from}T00:00:00Z`);
		if (to)     parts.push(`OccurredAt le ${to}T23:59:59Z`);

		const variant = VARIANTS[this.currentVariant] ?? {};
		if (variant.changeType) {
			parts.push(`ChangeType eq '${variant.changeType}'`);
		}
		if (variant.dateOffsetDays && !from) {
			const since = new Date(Date.now() - variant.dateOffsetDays * 24 * 60 * 60 * 1000).toISOString();
			parts.push(`OccurredAt ge ${since}`);
		}

		binding.changeParameters({ $filter: parts.length > 0 ? parts.join(" and ") : undefined });
	}
}

function escape(value: string): string {
	return value.replace(/'/g, "''");
}
